import React, {useEffect, useReducer, useState} from "react";
import localforage from "localforage";
import axios from "axios";
import Swal from "sweetalert2";
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import DashboardFooter from "../layouts/dashboard_footer";
import {useAppName, useBaseUrl} from "../../utils/utils";
import {Link} from "react-router-dom";
import VideoComm from "../../comms/VideoComm";

/**
 * Was a step-wizard shell where "Create" revealed an empty card and
 * "My Schedule" had no click handler at all - see
 * execution_strategy_activity_tracker.md for the rest of what "already
 * working on it" meant (broken WS route, broken serializer, no ASGI server
 * deployed). This fills in the same three steps for real against
 * uzima_comms' schedule/join/turn-credentials endpoints and the rewritten
 * MeetingConsumer.
 */

type MeetingType = { id: number; typey: string; desc: string };
type MeetingCategory = { id: number; category: string; description: string };
type Meeting = {
    id: number;
    meet_title: string;
    meet_date: string;
    meet_time: string;
    access_mode: 'closed' | 'general';
    meet_auth: { meet_id: string; meet_url: string };
    meet_type_detail: MeetingType;
};

const getStoredToken = () =>
    localStorage.getItem('token') || localStorage.getItem('authToken') || sessionStorage.getItem('token') || '';

const VideoChat: React.FC = () => {
    const initialState = {
        clinicName: "",
        appName: useAppName(),
        fname: "",
        pageTitle: "Telehealth App",
        step: 0,
    };

    const reducer = (state: any, action: any) => {
        switch (action.type) {
            case "SET_CLINIC_NAME":
                return {...state, clinicName: action.payload};
            case "SET_FIRST_NAME":
                return {...state, fname: action.payload};
            case "SET_STEP":
                return {...state, step: action.payload};
            case "SET_SUB_STEP":
                return {...state, subStep: action.payload};
            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(reducer, initialState);
    const baseUrl = useBaseUrl();

    const [types, setTypes] = useState<MeetingType[]>([]);
    const [categories, setCategories] = useState<MeetingCategory[]>([]);
    const [form, setForm] = useState({
        meet_title: '', meet_desc: '', meet_type: '', meet_categ: '',
        meet_date: '', meet_time: '', access_mode: 'closed' as 'closed' | 'general',
    });
    const [creating, setCreating] = useState(false);
    const [created, setCreated] = useState<{ meet_id: string; meet_url: string; meet_passcode: string | null } | null>(null);

    const [upcoming, setUpcoming] = useState<Meeting[]>([]);
    const [past, setPast] = useState<Meeting[]>([]);
    const [loadingSchedule, setLoadingSchedule] = useState(false);

    const [joinCode, setJoinCode] = useState('');
    const [joinPasscode, setJoinPasscode] = useState('');
    const [activeCall, setActiveCall] = useState<{ meetId: string; passcode?: string; callType: 'audio' | 'video' } | null>(null);

    const authHeaders = () => ({Authorization: `Bearer ${getStoredToken()}`});

    useEffect(() => {
        document.title = "Uzima - Telehealth Tools";

        localforage.getItem("clinic_name").then((clini) => {
            dispatch({type: "SET_CLINIC_NAME", payload: clini});
        });

        localforage.getItem("fname").then((fnm: any) => {
            dispatch({type: "SET_FIRST_NAME", payload: fnm});
        });

        axios.get(`${baseUrl}/comms/meeting-types`).then((r) => setTypes(r.data?.data || []));
        axios.get(`${baseUrl}/comms/meeting-categories`).then((r) => setCategories(r.data?.data || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSchedule = () => {
        setLoadingSchedule(true);
        const body = new URLSearchParams({page: '1'});
        Promise.all([
            axios.post(`${baseUrl}/comms/upcoming-meetings`, body, {headers: authHeaders()}),
            axios.post(`${baseUrl}/comms/past-meetings`, {}, {headers: authHeaders()}),
        ]).then(([upcomingRes, pastRes]) => {
            setUpcoming(upcomingRes.data?.data?.meetings || []);
            setPast(pastRes.data?.data?.meetings || []);
        }).catch(() => {
            Swal.fire({icon: 'error', title: 'Error', text: 'Could not load your schedule.'});
        }).finally(() => setLoadingSchedule(false));
    };

    const submitCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreating(true);
        setCreated(null);
        try {
            const body = new URLSearchParams({...form});
            const response = await axios.post(`${baseUrl}/comms/create-meeting`, body, {headers: authHeaders()});
            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Could not create meeting');
            }
            const meeting = response.data.data;
            setCreated({
                meet_id: meeting.meet_auth.meet_id,
                meet_url: meeting.meet_auth.meet_url,
                meet_passcode: meeting.meet_passcode,
            });
        } catch (err: any) {
            Swal.fire({icon: 'error', title: 'Error', text: err?.response?.data?.message || err.message});
        } finally {
            setCreating(false);
        }
    };

    const submitJoin = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const body = new URLSearchParams({meet_id: joinCode, passcode: joinPasscode});
            const response = await axios.post(`${baseUrl}/comms/join-meeting`, body, {headers: authHeaders()});
            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Could not join meeting');
            }
            setActiveCall({
                meetId: response.data.data.meet_id,
                passcode: joinPasscode || undefined,
                callType: response.data.data.call_type,
            });
            dispatch({type: "SET_STEP", payload: 3});
        } catch (err: any) {
            Swal.fire({icon: 'error', title: 'Could not join', text: err?.response?.data?.message || err.message});
        }
    };

    if (state.step === 3 && activeCall) {
        return (
            <VideoComm
                meetId={activeCall.meetId}
                passcode={activeCall.passcode}
                callType={activeCall.callType}
                onEnd={() => {
                    setActiveCall(null);
                    dispatch({type: "SET_STEP", payload: 0});
                }}
            />
        );
    }

    return (
        <>
            <DashboardHeader></DashboardHeader>
            <SideMenu></SideMenu>

            <div className={"vertical-overlay"}></div>

            <div className={"main-content"}>
                <div className={"page-content"}>

                    <div className={"container-fluid"}>

                        <div className="row mt-2">
                            <div className="col-12">
                                <div
                                    className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0  ">{state.clinicName} - {state.pageTitle} &nbsp;
                                    </h4>

                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a
                                                href="javascript: void(0);">Dashboards</a></li>
                                            <li className="breadcrumb-item active">{state.pageTitle}</li>
                                        </ol>
                                    </div>

                                </div>
                            </div>
                        </div>

                        <div className={"row mt-4"}>
                            <div className={"col text-center"}>
                                <h4>Telehealth Tools</h4>
                            </div>
                        </div>

                        <div className={"row mt-4"}>
                            <div className={"col text-center"}>
                                <h6>An assortment of digital conferencing tools to enable remote communication and monitoring by users on this platform</h6>
                            </div>
                        </div>

                        {state.step === 0 && (
                            <div className={"row mt-4"}>

                                <div className={"col"}>
                                    <Link to={"/institution/home"} className="card card-animate" style={{borderRadius: "0%"}}>
                                        <div className="card-body p-4 text-center">
                                            <div className="mx-auto avatar-md mb-3">
                                                <img src={`${process.env.PUBLIC_URL}/assets/images/dashboard.png`} alt=""
                                                     className="img-fluid"/>
                                            </div>
                                            <h5 className="card-title mb-1">Dashboard</h5>
                                        </div>
                                    </Link>
                                </div>

                                <div className={"col"}>
                                    <div className="card card-animate" onClick={() => {
                                        dispatch({type: "SET_SUB_STEP", payload: 0});
                                        dispatch({type: "SET_STEP", payload: 1});
                                    }} style={{borderRadius: "0%", cursor: 'pointer'}}>
                                        <div className="card-body p-4 text-center">
                                            <div className="mx-auto avatar-md mb-3">
                                                <img src={`${process.env.PUBLIC_URL}/assets/images/create.png`} alt=""
                                                     className="img-fluid"/>
                                            </div>
                                            <h5 className="card-title mb-1">Create</h5>
                                        </div>
                                    </div>
                                </div>

                                <div className={"col"}>
                                    <div className="card card-animate" onClick={() => {
                                        loadSchedule();
                                        dispatch({type: "SET_STEP", payload: 2});
                                    }} style={{borderRadius: "0%", cursor: 'pointer'}}>
                                        <div className="card-body p-4 text-center">
                                            <div className="mx-auto avatar-md mb-3">
                                                <img src={`${process.env.PUBLIC_URL}/assets/images/list.png`} alt=""
                                                     className="img-fluid"/>
                                            </div>
                                            <h5 className="card-title mb-1">My Schedule</h5>
                                        </div>
                                    </div>
                                </div>

                                <div className={"col"}>
                                    <div className="card card-animate" onClick={() => dispatch({type: "SET_STEP", payload: 4})}
                                         style={{borderRadius: "0%", cursor: 'pointer'}}>
                                        <div className="card-body p-4 text-center">
                                            <div className="mx-auto avatar-md mb-3">
                                                <img src={`${process.env.PUBLIC_URL}/assets/images/create.png`} alt=""
                                                     className="img-fluid"/>
                                            </div>
                                            <h5 className="card-title mb-1">Join a Meeting</h5>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}

                        {state.step === 1 && (
                            <div className={"row mt-4"}>
                                <div className={"col-md-2"}></div>
                                <div className={"col-md-8"}>
                                    <div className={"card"}>
                                        <div className={"card-header d-flex justify-content-between align-items-center"}>
                                            <span>Schedule a meeting</span>
                                            <button className="btn btn-sm btn-light" onClick={() => dispatch({type: "SET_STEP", payload: 0})}>Back</button>
                                        </div>
                                        <div className={"card-body"}>
                                            {created ? (
                                                <div>
                                                    <p>Meeting scheduled. Share this link with participants:</p>
                                                    <p><code>{window.location.origin}{created.meet_url}</code></p>
                                                    {created.meet_passcode && (
                                                        <p>Passcode: <strong>{created.meet_passcode}</strong> (closed meeting - required to join)</p>
                                                    )}
                                                    <button className="btn btn-primary" onClick={() => {
                                                        setCreated(null);
                                                        dispatch({type: "SET_STEP", payload: 0});
                                                    }}>Done</button>
                                                </div>
                                            ) : (
                                                <form onSubmit={submitCreate}>
                                                    <div className="mb-2">
                                                        <label>Title</label>
                                                        <input className="form-control" required value={form.meet_title}
                                                               onChange={(e) => setForm({...form, meet_title: e.target.value})}/>
                                                    </div>
                                                    <div className="mb-2">
                                                        <label>Description</label>
                                                        <textarea className="form-control" value={form.meet_desc}
                                                                  onChange={(e) => setForm({...form, meet_desc: e.target.value})}/>
                                                    </div>
                                                    <div className="row mb-2">
                                                        <div className="col">
                                                            <label>Type</label>
                                                            <select className="form-control" required value={form.meet_type}
                                                                    onChange={(e) => setForm({...form, meet_type: e.target.value})}>
                                                                <option value="">Select...</option>
                                                                {types.map((t) => <option key={t.id} value={t.id}>{t.typey}</option>)}
                                                            </select>
                                                        </div>
                                                        <div className="col">
                                                            <label>Category</label>
                                                            <select className="form-control" required value={form.meet_categ}
                                                                    onChange={(e) => setForm({...form, meet_categ: e.target.value})}>
                                                                <option value="">Select...</option>
                                                                {categories.map((c) => <option key={c.id} value={c.id}>{c.category}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="row mb-2">
                                                        <div className="col">
                                                            <label>Date</label>
                                                            <input type="date" className="form-control" required value={form.meet_date}
                                                                   onChange={(e) => setForm({...form, meet_date: e.target.value})}/>
                                                        </div>
                                                        <div className="col">
                                                            <label>Time</label>
                                                            <input type="time" className="form-control" required value={form.meet_time}
                                                                   onChange={(e) => setForm({...form, meet_time: e.target.value})}/>
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label>Access</label>
                                                        <select className="form-control" value={form.access_mode}
                                                                onChange={(e) => setForm({...form, access_mode: e.target.value as 'closed' | 'general'})}>
                                                            <option value="closed">Closed - passcode required</option>
                                                            <option value="general">General - anyone with the link</option>
                                                        </select>
                                                    </div>
                                                    <button className="btn btn-primary" type="submit" disabled={creating}>
                                                        {creating ? 'Scheduling…' : 'Schedule meeting'}
                                                    </button>
                                                </form>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={"col-md-2"}></div>
                            </div>
                        )}

                        {state.step === 2 && (
                            <div className={"row mt-4"}>
                                <div className={"col-md-2"}></div>
                                <div className={"col-md-8"}>
                                    <div className={"card"}>
                                        <div className={"card-header d-flex justify-content-between align-items-center"}>
                                            <span>My schedule</span>
                                            <button className="btn btn-sm btn-light" onClick={() => dispatch({type: "SET_STEP", payload: 0})}>Back</button>
                                        </div>
                                        <div className={"card-body"}>
                                            {loadingSchedule ? <p>Loading…</p> : (
                                                <>
                                                    <h6>Upcoming</h6>
                                                    {upcoming.length === 0 && <p>No upcoming meetings.</p>}
                                                    {upcoming.map((m) => (
                                                        <div key={m.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                                                            <span>{m.meet_title} — {m.meet_date} {m.meet_time}</span>
                                                            <button className="btn btn-sm btn-success" onClick={() => {
                                                                setActiveCall({meetId: m.meet_auth.meet_id, callType: m.meet_type_detail?.typey === 'audio' ? 'audio' : 'video'});
                                                                dispatch({type: "SET_STEP", payload: 3});
                                                            }}>Join</button>
                                                        </div>
                                                    ))}
                                                    <h6 className="mt-3">Past</h6>
                                                    {past.length === 0 && <p>No past meetings.</p>}
                                                    {past.map((m) => (
                                                        <div key={m.id} className="border-bottom py-2">
                                                            {m.meet_title} — {m.meet_date} {m.meet_time}
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={"col-md-2"}></div>
                            </div>
                        )}

                        {state.step === 4 && (
                            <div className={"row mt-4"}>
                                <div className={"col-md-2"}></div>
                                <div className={"col-md-8"}>
                                    <div className={"card"}>
                                        <div className={"card-header d-flex justify-content-between align-items-center"}>
                                            <span>Join a meeting</span>
                                            <button className="btn btn-sm btn-light" onClick={() => dispatch({type: "SET_STEP", payload: 0})}>Back</button>
                                        </div>
                                        <div className={"card-body"}>
                                            <form onSubmit={submitJoin}>
                                                <div className="mb-2">
                                                    <label>Meeting ID</label>
                                                    <input className="form-control" required value={joinCode}
                                                           onChange={(e) => setJoinCode(e.target.value)}/>
                                                </div>
                                                <div className="mb-3">
                                                    <label>Passcode (if required)</label>
                                                    <input className="form-control" value={joinPasscode}
                                                           onChange={(e) => setJoinPasscode(e.target.value)}/>
                                                </div>
                                                <button className="btn btn-primary" type="submit">Join</button>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                                <div className={"col-md-2"}></div>
                            </div>
                        )}

                    </div>

                </div>

                <DashboardFooter></DashboardFooter>
            </div>
        </>
    );
};

export default VideoChat;
